/**
 * Воспроизведение чужой волны по чанкам эфира (MADR 0040). Чистая логика без DOM и сети: вход — события `LiveEvent`
 * и часы, выход — снимок боя для поля.
 *
 * Бой создаётся из `start` + `seed` того же движка, команды планируются на их тики, поэтому картинка совпадает с
 * игроком. Зритель идёт за игроком с отставанием `lagTicks`: цель = тик последнего чанка плюс прошедшее с него время
 * (пока у игрока идёт бой), не дальше. Новая волна, чанк с меньшим тиком или команда раньше текущего тика — бой
 * собирается заново и догоняется (детерминизм делает это дешёвым).
 */
import type { BalanceData, BattleSnapshot, LiveChunk, LiveEvent, LiveUi, TimedCommand } from "@newfungame/contracts";
import { TICK_MS } from "@newfungame/contracts";
import { createBattle, MAX_WAVE_TICKS, type Battle } from "@newfungame/engine";

export const LAG_TICKS = 30;
/** Потолок шагов за один вызов `advance`: дольше — значит часы разошлись, догоняем в следующем кадре. */
const MAX_STEPS = 2000;

export interface PlaybackStatus {
  player: { id: string; name: string } | null;
  /** Чанков нет: игрок не в игре. */
  offline: boolean;
  wave: number | null;
  /** Тик игрока по последнему чанку. */
  remoteTick: number;
  /** Тик зрителя. */
  localTick: number;
  ui: LiveUi | null;
  /** Когда пришёл последний чанк, мс Unix; `null` — ещё не было. */
  at: number | null;
  commands: readonly TimedCommand[];
}

export interface Playback {
  apply(event: LiveEvent, receivedAt: number): void;
  /** Догнать цель на момент `now` и вернуть снимок; `null`, пока боя нет. */
  advance(now: number): BattleSnapshot | null;
  status(): PlaybackStatus;
}

const sameCommand = (a: TimedCommand, b: TimedCommand) => JSON.stringify(a) === JSON.stringify(b);

export function createPlayback(balance: BalanceData, lagTicks = LAG_TICKS): Playback {
  let battle: Battle | null = null;
  let key = "";
  let scheduled: TimedCommand[] = [];
  let chunk: LiveChunk | null = null;
  let at: number | null = null;
  let player: PlaybackStatus["player"] = null;
  let snapshot: BattleSnapshot | null = null;

  const stepTo = (target: number): void => {
    if (!battle) return;
    const limit = Math.min(target, MAX_WAVE_TICKS);
    for (let i = 0; i < MAX_STEPS && battle.tick() < limit; i++) battle.step();
    snapshot = battle.snapshot();
  };

  const rebuild = (c: LiveChunk): void => {
    battle = createBattle({ balance, run: c.start, seed: c.seed, autoSkillsAllowed: c.vip ?? false });
    for (const cmd of c.commands) battle.schedule(cmd);
    scheduled = [...c.commands];
    stepTo(c.tick);
  };

  return {
    apply(event, receivedAt) {
      player = event.player;
      if (!event.chunk) {
        chunk = null;
        return;
      }
      const c = event.chunk;
      chunk = c;
      at = receivedAt;
      const k = `${c.runId}|${c.wave}|${c.seed}`;
      const prefixKept =
        c.commands.length >= scheduled.length && scheduled.every((cmd, i) => sameCommand(cmd, c.commands[i]!));
      const fresh = c.commands.slice(scheduled.length);
      const tick = battle?.tick() ?? 0;
      if (!battle || k !== key || c.tick < tick || !prefixKept || fresh.some((cmd) => cmd.tick < tick)) {
        key = k;
        rebuild(c);
        return;
      }
      for (const cmd of fresh) battle.schedule(cmd);
      scheduled.push(...fresh);
    },
    advance(now) {
      if (!battle || !chunk || at === null) return snapshot;
      const elapsed = chunk.ui.state === "battle" ? Math.floor((now - at) / TICK_MS) : 0;
      const target = Math.max(battle.tick(), chunk.tick + elapsed - lagTicks);
      stepTo(target);
      return snapshot;
    },
    status: () => ({
      player,
      offline: chunk === null,
      wave: chunk?.wave ?? null,
      remoteTick: chunk?.tick ?? 0,
      localTick: battle?.tick() ?? 0,
      ui: chunk?.ui ?? null,
      at,
      commands: scheduled,
    }),
  };
}
